// Audio synthesis helper using Web Audio API to generate premium, cozy sounds
// and control volume without external assets.

let audioCtx = null;

const getAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// Create a gain node to control the master volume
const createGainNode = (ctx, volume) => {
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(volume, ctx.currentTime);
  gainNode.connect(ctx.destination);
  return gainNode;
};

// 1. Classic Bell Sound
const playClassicBell = (ctx, destination) => {
  const now = ctx.currentTime;
  
  // Fundamental frequency and major harmonics
  const freqs = [587.33, 880.00, 1174.66];
  const gains = [0.6, 0.3, 0.1];
  
  freqs.forEach((freq, index) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    
    // Bell envelope: instant attack, long exponential decay
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(gains[index], now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.5);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(now);
    osc.stop(now + 2.6);
  });
};

// 2. Rain Bell Sound (ambient noise + bell)
const playRainBell = (ctx, destination) => {
  const now = ctx.currentTime;
  const duration = 3.0;
  
  // Create rain noise
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  // Fill buffer with white noise
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  
  const noiseNode = ctx.createBufferSource();
  noiseNode.buffer = buffer;
  
  // Filter the noise to make it sound like gentle rain (bandpass filter)
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1000, now);
  filter.Q.setValueAtTime(0.5, now);
  
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, now);
  noiseGain.gain.linearRampToValueAtTime(0.15, now + 0.5); // Fade in rain
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + duration); // Fade out rain
  
  noiseNode.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(destination);
  
  noiseNode.start(now);
  noiseNode.stop(now + duration);
  
  // Play a soft bell chime 0.3 seconds in
  setTimeout(() => {
    if (ctx.state === 'closed') return;
    const chimeTime = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(783.99, chimeTime); // G5
    
    gain.gain.setValueAtTime(0, chimeTime);
    gain.gain.linearRampToValueAtTime(0.4, chimeTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, chimeTime + 2.0);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(chimeTime);
    osc.stop(chimeTime + 2.1);
  }, 300);
};

// 3. Cafe Chime
const playCafeChime = (ctx, destination) => {
  const now = ctx.currentTime;
  
  // Cozy low-frequency dual-tone chime
  const freqs = [329.63, 440.00]; // E4, A4
  const gains = [0.5, 0.4];
  
  freqs.forEach((freq, index) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'triangle'; // triangle wave gives a warmer sound
    osc.frequency.setValueAtTime(freq, now);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(gains[index], now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.0);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(now);
    osc.stop(now + 2.1);
  });
};

// 4. Wind Chime
const playWindChime = (ctx, destination) => {
  const now = ctx.currentTime;
  
  // Play a series of 4 sparkling high-pitched tones at random slight offsets
  const notes = [1046.50, 1174.66, 1318.51, 1567.98]; // C6, D6, E6, G6
  
  notes.forEach((freq, idx) => {
    const startDelay = idx * 0.15 + Math.random() * 0.1;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + startDelay);
    
    gain.gain.setValueAtTime(0, now + startDelay);
    gain.gain.linearRampToValueAtTime(0.25, now + startDelay + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + startDelay + 1.8);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(now + startDelay);
    osc.stop(now + startDelay + 1.9);
  });
};

// 5. Soft Notification
const playSoftNotification = (ctx, destination) => {
  const now = ctx.currentTime;
  
  // Friendly rising arpeggio: C5 -> E5 -> G5 -> C6
  const notes = [523.25, 659.25, 783.99, 1046.50];
  
  notes.forEach((freq, idx) => {
    const noteStart = now + idx * 0.08;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, noteStart);
    
    gain.gain.setValueAtTime(0, noteStart);
    gain.gain.linearRampToValueAtTime(0.3, noteStart + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + 0.4);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(noteStart);
    osc.stop(noteStart + 0.5);
  });
};

// 6. Digital Bell
const playDigitalBell = (ctx, destination) => {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = 'square';
  osc.frequency.setValueAtTime(880, now); // A5
  
  // Short sharp beep
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
  
  osc.connect(gain);
  gain.connect(destination);
  
  osc.start(now);
  osc.stop(now + 0.7);
};

// 7. Forest Bird
const playForestBird = (ctx, destination) => {
  const now = ctx.currentTime;
  
  const chirp = (startTime) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    // Frequency sweeps upward rapidly
    osc.frequency.setValueAtTime(1600, startTime);
    osc.frequency.exponentialRampToValueAtTime(3200, startTime + 0.12);
    
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.15);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(startTime);
    osc.stop(startTime + 0.16);
  };
  
  chirp(now);
  chirp(now + 0.18);
  chirp(now + 0.36);
};

// 8. Keyboard Click
const playKeyboardClick = (ctx, destination) => {
  const now = ctx.currentTime;
  const bufferSize = ctx.sampleRate * 0.05; // 50ms click
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1600, now);
  filter.Q.setValueAtTime(3.0, now);
  
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
  
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(destination);
  
  noise.start(now);
  noise.stop(now + 0.05);
};

// 9. Gentle Piano
const playGentlePiano = (ctx, destination) => {
  const now = ctx.currentTime;
  
  // Cozy Piano Chord: C4, E4, G4, C5
  const notes = [261.63, 329.63, 392.00, 523.25];
  
  notes.forEach((freq) => {
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator(); // Add a second oscillator for richer harmonics
    const gain = ctx.createGain();
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, now);
    
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, now); // Second harmonic
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.08); // Slow piano-like attack
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 3.0); // Nice long decay
    
    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(destination);
    
    osc.start(now);
    osc2.start(now);
    osc.stop(now + 3.1);
    osc2.stop(now + 3.1);
  });
};

export const playAlarm = (soundId, volume = 0.5) => {
  try {
    const ctx = getAudioContext();
    const destination = createGainNode(ctx, volume);
    
    switch (soundId) {
      case 'classic-bell':
        playClassicBell(ctx, destination);
        break;
      case 'rain-bell':
        playRainBell(ctx, destination);
        break;
      case 'cafe-chime':
        playCafeChime(ctx, destination);
        break;
      case 'wind-chime':
        playWindChime(ctx, destination);
        break;
      case 'soft-notification':
        playSoftNotification(ctx, destination);
        break;
      case 'digital-bell':
        playDigitalBell(ctx, destination);
        break;
      case 'forest-bird':
        playForestBird(ctx, destination);
        break;
      case 'keyboard-click':
        playKeyboardClick(ctx, destination);
        break;
      case 'gentle-piano':
        playGentlePiano(ctx, destination);
        break;
      case 'classic-alarm': // Fallback to play the local mp3 file
      default: {
        const audio = new Audio("/alert/raceTimer.mp3");
        audio.volume = volume;
        audio.play().catch(err => console.log('Audio file playback failed:', err));
        break;
      }
    }
  } catch (error) {
    console.error('Failed to play alarm sound:', error);
  }
};

export const ALARMS = [
  { id: 'classic-alarm', name: '🔔 Classic Alarm (Default)' },
  { id: 'classic-bell', name: '🔔 Classic Bell' },
  { id: 'rain-bell', name: '🌧 Rain Bell' },
  { id: 'cafe-chime', name: '☕ Cafe Chime' },
  { id: 'wind-chime', name: '🎐 Wind Chime' },
  { id: 'soft-notification', name: '✨ Soft Notification' },
  { id: 'digital-bell', name: '🎵 Digital Bell' },
  { id: 'forest-bird', name: '🐦 Forest Bird' },
  { id: 'keyboard-click', name: '⌨️ Keyboard Click' },
  { id: 'gentle-piano', name: '🎹 Gentle Piano' }
];
